import { z } from 'zod';
import { zObject } from '../util/zod';

export const msTenantsSchema = z.union([
    z.literal('groups'),
    z.literal('sites'),
    z.literal('onedrive'),
    z.literal('group'),
    z.literal('documents'),
    z.literal('me'),
]);
export type MSTenants = z.infer<typeof msTenantsSchema>;

export type BaseEntity = {
    title: string;
    id: string;
    type: string;
};
export const baseEntitySchema = zObject<BaseEntity>({
    title: z.string(),
    id: z.string(),
    type: z.string(),
});

export const msTenantEntitySchema = baseEntitySchema.omit({ id: true }).extend({
    name: msTenantsSchema.optional(),
});
export type MSTenantEntity = z.infer<typeof msTenantEntitySchema>;

export const msSiteEntitySchema = baseEntitySchema.and(z.object({}));
export type MSSiteEntity = z.infer<typeof msSiteEntitySchema>;

export const msGroupEntitySchema = baseEntitySchema.extend({
    visibility: z.union([z.literal('public'), z.literal('private')]).optional(),
});
export type MSGroupEntity = z.infer<typeof msGroupEntitySchema>;

export const documentEntitySchema = baseEntitySchema.extend({
    mimetype: z.string().optional(),
    webUrl: z.string().optional(),
    size: z.number().optional(),
});
export type DocumentEntity = z.infer<typeof documentEntitySchema>;

export const msDocumentEntitySchema = documentEntitySchema;
export type MSDocumentEntity = z.infer<typeof msDocumentEntitySchema>;

export const dropboxDocumentEntitySchema = documentEntitySchema.extend({
    _extend: z.object({
        path: z.string(),
    }),
});
export type DropboxDocumentEntity = z.infer<typeof dropboxDocumentEntitySchema>;

export const msUnifiedEntitySchema = msGroupEntitySchema.and(documentEntitySchema);
export type MSUnifiedEntity = z.infer<typeof msUnifiedEntitySchema>;

export const driveAccountSchema = z.object({
    id: z.string(),
    title: z.string(),
    mail: z.string(),
    default: z.boolean().optional(),
});
export type DriveAccount = z.infer<typeof driveAccountSchema>;

export const driveItemTypeSchema = z.object({
    title: z.string(),
    id: z.string(),
    type: z.string(),
    mimetype: z.string().optional(),
    webUrl: z.string().optional(),
});
export type DriveItemType = z.infer<typeof driveItemTypeSchema>;

export const msDocumentParamsSchema = z.object({
    msTenant: z.union([msTenantsSchema, z.undefined()]), // Microsoft Graph Only
    msId: z.union([z.string(), z.undefined()]), // Microsoft Graph Only
    idFolder: z.union([z.string(), z.undefined()]),
    page: z.number(),
    limit: z.number(),
    query: z.string().optional().nullable(),
});
export type MSDocumentParams = z.infer<typeof msDocumentParamsSchema>;

export const dropboxDocumentParamsSchema = z.object({
    path_lower: z.string(),
    query: z.string().optional().nullable(),
});
export type DropboxDocumentParams = z.infer<typeof dropboxDocumentParamsSchema>;

export const driveTmpFileSchema = z.object({
    id: z.number(),
    name: z.string(),
    type: z.string(),
    token: z.string(),
    size: z.number(),
    mediaKey: z.string(),
});
export type DriveTmpFile = z.infer<typeof driveTmpFileSchema>;

export const driveGetDocumentParamsSchema = z.object({
    token: z.string().optional(),
    attrName: z.string(),
    file: z.object({
        service: z.string(),
        account: z.string(),
        title: z.string(),
        mimetype: z.string().optional(),
        _document: documentEntitySchema,
    }),
});
export type DriveGetDocumentParams = z.infer<typeof driveGetDocumentParamsSchema>;

export const driveLogoutParamsSchema = z.object({
    id: z.string(),
    service: z.string(),
});
export type DriveLogoutParams = z.infer<typeof driveLogoutParamsSchema>;

export const driveGetAccountParamsSchema = z.object({
    service: z.string(),
    id: z.string(),
});
export type DriveGetAccountParams = z.infer<typeof driveGetAccountParamsSchema>;

export const driveGetAccountSchema = z.object({
    account: driveAccountSchema,
});
export type DriveGetAccount = z.infer<typeof driveGetAccountSchema>;

export const driveSetDefaultAccountParamsSchema = z.object({
    service: z.string(),
    account: z.string(),
});
export type DriveSetDefaultAccountParams = z.infer<typeof driveSetDefaultAccountParamsSchema>;

export const driveGetTenantsParamsSchema = z.object({
    allow: z
        .union([z.literal('groups'), z.literal('me')])
        .array()
        .optional(),
    id: z.string(),
});
export type DriveGetTenantsParams = z.infer<typeof driveGetTenantsParamsSchema>;

export const msGetDocumentsParamsSchema = z.object({
    id: z.string(),
    documentParams: msDocumentParamsSchema,
});
export type MSGetDocumentsParams = z.infer<typeof msGetDocumentsParamsSchema>;

export const dropboxGetDocumentsParamsSchema = z.object({
    id: z.string(),
    documentParams: dropboxDocumentParamsSchema,
});
export type DropboxGetDocumentsParams = z.infer<typeof dropboxGetDocumentsParamsSchema>;

export const driveGetGroupsParamsSchema = z.object({
    query: z.string().optional(),
    filter: z.string().optional(),
    id: z.string(),
});
export type DriveGetGroupsParams = z.infer<typeof driveGetGroupsParamsSchema>;
